// Login Page — Admin authentication
import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { PolesteadLockup } from '@/lib/PolesteadMark';
import { loginReasonMessage, readLoginReason } from '@/lib/session-signout';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Self-serve password recovery. 'signin' = normal login; 'forgot' = email a
  // reset link; 'sent' = neutral confirmation (never leak whether an email exists).
  const [mode, setMode] = useState<'signin' | 'forgot' | 'sent'>('signin');
  // D-52 / 19H §0.8: why this person is here after a sign-out they did not ask for
  // (an hour idle, 24 hours old, or ended from another device). Two locked strings;
  // anything else in the query string renders nothing.
  const [signedOutMessage] = useState(() => loginReasonMessage(readLoginReason(window.location.search)));

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
    } catch {
      /* neutral response regardless of outcome */
    } finally {
      setLoading(false);
      setMode('sent');
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-secondary)',
    }}>
      <div style={{
        width: '400px', background: 'var(--surface)', borderRadius: '16px', padding: '40px',
        boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <PolesteadLockup stacked markSize={48} fontSize={26} gap={12} style={{ marginBottom: 10 }} />
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            {mode === 'signin' ? 'License Server Administration' : 'Reset your password'}
          </p>
        </div>

        {signedOutMessage && mode === 'signin' && (
          <div
            role="status"
            style={{
              padding: '10px 12px', borderRadius: '8px', background: 'var(--bg-secondary)',
              border: '1px solid var(--border)', color: 'var(--text-primary)',
              fontSize: '13px', marginBottom: '20px', lineHeight: 1.5,
            }}
          >
            {signedOutMessage}
          </div>
        )}

        {mode === 'sent' ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              padding: '12px', borderRadius: '8px', background: '#ECFDF5',
              color: '#047857', fontSize: '13px', marginBottom: '20px', lineHeight: 1.5,
            }}>
              If an account exists for <strong>{email}</strong>, a password-reset link is on
              its way. Check your inbox.
            </div>
            <button
              onClick={() => { setMode('signin'); setError(''); }}
              style={{
                background: 'none', border: 'none', color: 'var(--accent)',
                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={mode === 'forgot' ? handleForgot : handleLogin}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@polestead.com"
                required
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: '1px solid var(--border)', fontSize: '14px', outline: 'none',
                }}
              />
            </div>

            {mode === 'signin' && (
              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px',
                    border: '1px solid var(--border)', fontSize: '14px', outline: 'none',
                  }}
                />
              </div>
            )}

            <div style={{ textAlign: 'right', marginBottom: '20px' }}>
              <button
                type="button"
                onClick={() => { setMode(mode === 'forgot' ? 'signin' : 'forgot'); setError(''); }}
                style={{
                  background: 'none', border: 'none', color: 'var(--accent)',
                  fontSize: '12px', fontWeight: 500, cursor: 'pointer', padding: 0,
                }}
              >
                {mode === 'forgot' ? '← Back to sign in' : 'Forgot your password?'}
              </button>
            </div>

            {error && (
              <div style={{
                padding: '10px 12px', borderRadius: '6px', background: '#FEE2E2',
                color: '#DC2626', fontSize: '13px', marginBottom: '16px',
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '11px', borderRadius: '8px',
                background: loading ? 'var(--accent-muted)' : 'var(--accent)',
                color: 'white', fontSize: '14px', fontWeight: 600,
                border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading
                ? (mode === 'forgot' ? 'Sending…' : 'Signing in...')
                : (mode === 'forgot' ? 'Send reset link' : 'Sign In')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
